import { useState, useEffect } from 'react'
import { Outlet, Link, useNavigate, useLocation, Navigate } from 'react-router-dom'
import { useAuthStore } from '../stores/authStore'
import { Home, Package, ClipboardList, Bell, LogOut, Settings, FileText, Wrench, Users, BarChart3, CheckSquare, Truck, QrCode, ChevronDown, Building2, Trash2, Cog, FileCheck, Keyboard, Search, User, HelpCircle } from 'lucide-react'
import { getMenuByRole, isRouteRegistered } from '../config/menuRegistry'
import NotificationBell from './NotificationBell'
import UserProfileModal from './UserProfileModal'
import { CamsLogoMark } from './CamsLogo'
import { useApprovalInboxSummary } from '../approval-kit/hooks/useApprovalState'
import { useNotificationCount } from '../hooks/queries/useNotifications'
import useAppBadge from '../hooks/useAppBadge'
import { isMobileDevice } from '../utils/deviceDetect'
import ErrorBoundary from './ErrorBoundary'
import SwUpdateBanner from './SwUpdateBanner'
// Phase O 거버넌스: window.confirm 대체
import { confirmAction, toastError } from '../lib/toast'

export default function Layout() {
  const { user, logout } = useAuthStore()
  const navigate = useNavigate()
  const location = useLocation()
  const [showShortcuts, setShowShortcuts] = useState(false)
  const [showProfileModal, setShowProfileModal] = useState(false)
  const [openSubmenu, setOpenSubmenu] = useState(null)
  const [showHelp, setShowHelp] = useState(false)

  // 키보드 단축키
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ctrl/Cmd + 키 조합
      if (e.ctrlKey || e.metaKey) {
        switch (e.key) {
          case 'h':
            e.preventDefault()
            navigate('/')
            break
          case 'm':
            e.preventDefault()
            navigate('/molds')
            break
          case 'r':
            e.preventDefault()
            navigate('/reports')
            break
          case 'k':
            e.preventDefault()
            setShowShortcuts(prev => !prev)
            break
        }
      }
      // ESC로 단축키 도움말 닫기
      if (e.key === 'Escape') {
        setShowShortcuts(false)
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [navigate])

  // 모바일 디바이스에서 데스크톱 라우트 접근 시 → 모바일 홈으로 리다이렉트
  // ⚠️ 모든 hooks 호출 이후에 위치해야 함 (React hooks 규칙)
  if (isMobileDevice()) {
    return <Navigate to="/mobile/home" replace />
  }

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  // menuRegistry 기반 메뉴 구성
  // - 단일 capability 협력사: 자동 활성화된 역할 메뉴
  // - 양쪽 capability 협력사: active_role 기반 메뉴 (선택 후만 표시)
  // - 관리자/담당자: user_type 기반 (변경 없음)
  const getMenuItems = () => {
    const ADMIN_ROLES = ['system_admin', 'mold_developer', 'staff']
    const isAdmin = ADMIN_ROLES.includes(user?.user_type)
    const caps = Array.isArray(user?.company?.capabilities) && user.company.capabilities.length > 0
      ? user.company.capabilities
      : null

    let menuConfig = []
    if (isAdmin) {
      menuConfig = getMenuByRole(user?.user_type)
    } else if (!caps) {
      // capabilities 미설정 (하위 호환) → user_type 단일 메뉴
      menuConfig = getMenuByRole(user?.user_type)
    } else if (caps.length === 1) {
      // 단일 capability — 자동 결정
      menuConfig = getMenuByRole(caps[0])
    } else if (user?.active_role) {
      // 양쪽 capability + 선택 완료
      menuConfig = getMenuByRole(user.active_role)
    } else {
      // 양쪽 capability + 미선택 → 메뉴 비워두기 (RoleChooserModal 이 표시되어야 함)
      menuConfig = []
    }

    // menuRegistry 형식을 Layout 형식으로 변환 + 중복 label/path 제거
    const seen = new Set()
    return menuConfig
      .filter(item => {
        const key = item.label + '|' + item.path
        if (seen.has(key)) return false
        seen.add(key)
        return true
      })
      .map(item => ({
        to: item.path,
        icon: item.icon,
        label: item.label,
        badge: item.badge,
        // subMenu 가 allowedRoles 를 선언한 경우에만 user_type 으로 거른다.
        // (선언하지 않은 기존 항목은 지금까지와 똑같이 그대로 노출 — 예: staff 는 MOLD_DEVELOPER_MENU 를
        //  공유하므로, staff 에게만 감출 항목은 subMenu 에 allowedRoles 를 달아 준다)
        subMenus: item.subMenus
          ?.filter(sub => !sub.allowedRoles || sub.allowedRoles.includes(user?.user_type))
          .map(sub => ({
            to: sub.path,
            label: sub.label
          })) || []
      }))
  }

  const menuItems = getMenuItems()

  // approval-kit 대기 건수 (badge 표시용)
  const { data: approvalSummary } = useApprovalInboxSummary()
  const pendingApprovals = approvalSummary?.pending_count || 0
  const { data: unreadNotificationsCount = 0 } = useNotificationCount()

  // 양쪽 capability 협력사: active_role 과 현재 대시보드 URL 불일치 시 자동 리다이렉트
  useEffect(() => {
    const ADMIN = ['system_admin', 'mold_developer', 'staff']
    if (!user || ADMIN.includes(user.user_type)) return
    const role = user.active_role
    if (!role) return
    const path = location.pathname
    // 역할 대시보드 루트(/dashboard/plant | /dashboard/maker)에서만 정렬 —
    //   겸업 협력사가 반대 역할 대시보드에 있을 때 active_role 대시보드로 이동.
    //   ⚠ /dashboard/system-admin/* 등 다른 대시보드 하위 페이지(운영 입력현황 등)는
    //      건드리지 않는다 (2026-07-30: partner-operations 접근 시 튕김 버그 수정).
    if (path === '/dashboard/plant' || path === '/dashboard/maker') {
      const expectedPath = role === 'plant' ? '/dashboard/plant'
                         : role === 'maker' ? '/dashboard/maker'
                         : null
      if (expectedPath && path !== expectedPath) {
        navigate(expectedPath, { replace: true })
      }
    }
  }, [user?.active_role, location.pathname, navigate, user])

  // PWA 앱 아이콘 배지 — 미읽음 알림 + 승인 대기 (모바일 셸과 동일 훅)
  useAppBadge(pendingApprovals)

  const getBadgeValue = (badgeKey) => {
    if (badgeKey === 'pendingApprovals') return pendingApprovals
    if (badgeKey === 'unreadNotifications') return unreadNotificationsCount
    return 0
  }

  const isActive = (path) => {
    const p = location.pathname
    // '/' → 대시보드 redirect 페이지: 대시보드 메뉴에서 active
    if (path === '/') return p === '/'
    // 대시보드 경로: 정확 매칭만 (승인함, 모니터링 등과 구분)
    if (path.match(/^\/dashboard\/[^/]+$/)) return p === path
    // /dashboard/ 하위 메뉴: prefix 매칭
    if (path.startsWith('/dashboard/')) return p === path || p.startsWith(path + '/')
    // 일반 메뉴: 정확 매칭 또는 하위 경로
    return p === path || p.startsWith(path + '/') || p.startsWith(path + '?')
  }

  // 사용자 유형별 표시 이름 (active_role 우선 — 양쪽 capability 협력사)
  const getUserTypeLabel = () => {
    const labels = {
      'system_admin': '시스템관리자',
      'mold_developer': '관리자',
      'staff': '관리자',   // Phase GJ: staff 도 admin 권한 동등 (Phase N) → 라벨도 '관리자' 통일
      'maker': '제작처',
      'plant': '생산처'
    }
    const ADMIN = ['system_admin', 'mold_developer', 'staff']
    const effective = ADMIN.includes(user?.user_type)
      ? user.user_type
      : (user?.active_role || user?.user_type)
    return labels[effective] || '사용자'
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Phase CL-B M-2: SW 업데이트 안내 배너 (이벤트 수신 시에만 노출) */}
      <SwUpdateBanner />
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-[300]">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-14">
            <div className="flex items-center space-x-3 shrink-0">
              {/* Phase HZ: CAMS 회사 로고 */}
              <CamsLogoMark size={36} />
              <div className="leading-tight">
                <h1 className="text-lg font-bold text-gray-900">CAMS</h1>
                <span className="text-[10px] text-gray-400">금형관리 시스템</span>
              </div>
              <span className="hidden md:inline-block px-2.5 py-0.5 bg-blue-100 text-blue-800 text-[11px] font-medium rounded-full">
                {getUserTypeLabel()}
              </span>
            </div>
            <div className="flex items-center space-x-1">
              <button
                onClick={() => setShowHelp(true)}
                className="p-2 text-gray-400 hover:text-blue-600 hover:bg-gray-100 rounded-lg transition-colors"
                title="시스템 도움말"
              >
                <HelpCircle size={18} />
              </button>
              <button
                onClick={() => setShowProfileModal(true)}
                className="flex items-center space-x-2 px-3 py-2 hover:bg-gray-100 rounded-lg transition-colors group"
                title="내 정보 관리"
              >
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                  <User size={16} className="text-blue-600" />
                </div>
                <div className="text-right hidden sm:block">
                  <p className="text-sm font-medium text-gray-900 group-hover:text-blue-600">{user?.name}</p>
                  <p className="text-xs text-gray-500">{user?.company_name}</p>
                </div>
              </button>
              {/* 양쪽 capability 협력사 — 현재 역할 배지 + 변경 */}
              {(() => {
                const ADMIN = ['system_admin', 'mold_developer', 'staff']
                const isAdmin = ADMIN.includes(user?.user_type)
                const caps = user?.company?.capabilities
                const isDual = !isAdmin && Array.isArray(caps) && caps.length >= 2
                if (!isDual) return null
                const role = user?.active_role
                const label = role === 'plant' ? '🏭 생산처' : role === 'maker' ? '⚙️ 제작처' : '역할 선택'
                const colorClass = role === 'plant'
                  ? 'bg-blue-100 text-blue-700 border-blue-300'
                  : role === 'maker'
                  ? 'bg-purple-100 text-purple-700 border-purple-300'
                  : 'bg-gray-100 text-gray-700 border-gray-300'
                return (
                  <button
                    onClick={async () => {
                      const next = role === 'plant' ? 'maker' : 'plant'
                      const ok = await confirmAction(`${next === 'plant' ? '🏭 생산처' : '⚙️ 제작처'} 역할로 전환하시겠습니까?`, { title: '역할 전환', confirmText: '전환' })
                      if (!ok) return
                      const { selectRole } = useAuthStore.getState()
                      const r = await selectRole(next)
                      if (r.success) {
                        // 새 역할의 대시보드로 hard navigation (전체 새로고침 + auth 갱신)
                        const newPath = next === 'plant' ? '/dashboard/plant' : '/dashboard/maker'
                        window.location.href = newPath
                      } else {
                        toastError(r.error || '역할 전환 실패')
                      }
                    }}
                    className={`hidden sm:inline-flex items-center px-2.5 py-1 text-xs font-medium border rounded-md ${colorClass} hover:opacity-80`}
                    title="역할 변경"
                  >
                    {label}
                  </button>
                )
              })()}
              <button
                onClick={handleLogout}
                className="flex items-center space-x-1 px-3 py-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                title="로그아웃"
              >
                <LogOut size={18} />
                <span className="text-sm hidden sm:inline">로그아웃</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="bg-white border-b sticky top-14 z-[250]">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
          {/* 2026-08-18: 줄바꿈 금지 — 한 줄 유지. overflow-x-auto 는 하위 드롭다운(absolute)을 잘라 안 보이게 하므로 사용 금지 (overflow-visible) */}
          <div className="flex flex-nowrap justify-center overflow-visible">
            {menuItems.map((item, index) => {
              const Icon = item.icon
              const active = isActive(item.to)
              const hasSubmenu = item.subMenus && item.subMenus.length > 0
              const isOpen = openSubmenu === index
              
              return (
                <div 
                  key={item.to} 
                  className="relative"
                  onMouseEnter={() => hasSubmenu && setOpenSubmenu(index)}
                  onMouseLeave={() => hasSubmenu && setOpenSubmenu(null)}
                >
                  <button
                    onClick={() => {
                      if (!hasSubmenu) {
                        navigate(item.to)
                      }
                    }}
                    className={`relative flex items-center px-3 xl:px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                      active
                        ? 'text-blue-600 border-blue-600'
                        : 'text-gray-700 border-transparent hover:text-blue-600 hover:border-blue-300'
                    }`}
                  >
                    <Icon size={16} className="mr-1.5" />
                    {item.label}
                    {/* 배지는 absolute — 메뉴 폭 변화 없음 (줄바꿈 원인 제거) */}
                    {item.badge && getBadgeValue(item.badge) > 0 && (
                      <span className="absolute top-1 right-0.5 inline-flex items-center justify-center min-w-[16px] h-[16px] px-1 bg-red-600 text-white text-[10px] font-bold rounded-full leading-none">
                        {getBadgeValue(item.badge) > 99 ? '99+' : getBadgeValue(item.badge)}
                      </span>
                    )}
                    {hasSubmenu && (
                      <ChevronDown
                        size={16}
                        className={`ml-1 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                      />
                    )}
                  </button>
                  
                  {/* Submenu Dropdown */}
                  {hasSubmenu && isOpen && (
                    <div className="absolute top-full left-0 mt-0 w-56 bg-white border border-gray-200 rounded-b-lg shadow-xl z-[400]">
                      {item.subMenus.map((subItem) => (
                        <Link
                          key={subItem.to}
                          to={subItem.to}
                          onClick={() => setOpenSubmenu(null)}
                          className={`block px-4 py-3 text-sm hover:bg-blue-50 transition-colors border-b border-gray-100 last:border-b-0 ${
                            location.pathname === subItem.to
                              ? 'text-blue-600 bg-blue-50 font-medium'
                              : 'text-gray-700'
                          }`}
                        >
                          <span className="flex items-center">
                            <span className="w-1.5 h-1.5 rounded-full bg-gray-400 mr-2"></span>
                            {subItem.label}
                          </span>
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <ErrorBoundary homeUrl="/">
          <Outlet />
        </ErrorBoundary>
      </main>

      {/* 프로필 수정 모달 */}
      <UserProfileModal isOpen={showProfileModal} onClose={() => setShowProfileModal(false)} />

      {/* 키보드 단축키 도움말 모달 */}
      {showShortcuts && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[400]" onClick={() => setShowShortcuts(false)}>
          <div className="bg-white rounded-xl shadow-2xl p-6 max-w-md w-full mx-4" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Keyboard size={20} className="text-blue-600" />
                키보드 단축키
              </h3>
              <button onClick={() => setShowShortcuts(false)} className="text-gray-400 hover:text-gray-600">
                ✕
              </button>
            </div>
            <div className="space-y-3">
              <div className="flex items-center justify-between py-2 border-b">
                <span className="text-gray-600">대시보드</span>
                <kbd className="px-2 py-1 bg-gray-100 rounded text-sm font-mono">Ctrl + H</kbd>
              </div>
              <div className="flex items-center justify-between py-2 border-b">
                <span className="text-gray-600">금형 목록</span>
                <kbd className="px-2 py-1 bg-gray-100 rounded text-sm font-mono">Ctrl + M</kbd>
              </div>
              <div className="flex items-center justify-between py-2 border-b">
                <span className="text-gray-600">리포트</span>
                <kbd className="px-2 py-1 bg-gray-100 rounded text-sm font-mono">Ctrl + R</kbd>
              </div>
              <div className="flex items-center justify-between py-2 border-b">
                <span className="text-gray-600">단축키 도움말</span>
                <kbd className="px-2 py-1 bg-gray-100 rounded text-sm font-mono">Ctrl + K</kbd>
              </div>
              <div className="flex items-center justify-between py-2">
                <span className="text-gray-600">닫기</span>
                <kbd className="px-2 py-1 bg-gray-100 rounded text-sm font-mono">ESC</kbd>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 시스템 도움말 모달 */}
      {showHelp && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[400]" onClick={() => setShowHelp(false)}>
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <HelpCircle size={20} className="text-blue-600" />
                CAMS 시스템 도움말
              </h3>
              <button onClick={() => setShowHelp(false)} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
            </div>
            <div className="p-5 overflow-y-auto space-y-5">
              {/* 시스템 개요 */}
              <section>
                <h4 className="text-sm font-bold text-gray-900 mb-2 flex items-center gap-1.5">📋 시스템 개요</h4>
                <p className="text-sm text-gray-600 leading-relaxed">
                  CAMS(금형관리 시스템)는 자동차 부품 금형의 전 수명주기를 관리합니다.
                  금형 등록부터 개발, 양산, 유지보전, 이관, 폐기까지 모든 과정을 추적하고 관리할 수 있습니다.
                </p>
              </section>

              {/* 주요 기능 */}
              <section>
                <h4 className="text-sm font-bold text-gray-900 mb-2 flex items-center gap-1.5">⚡ 주요 기능</h4>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { icon: '🏭', title: '금형 등록/관리', desc: '신규 금형 등록, 일괄 등록, QR 코드 자동 생성' },
                    { icon: '📊', title: '개발 진행현황', desc: '12단계 공정별 진행률 실시간 모니터링' },
                    { icon: '🔧', title: '수리/유지보전', desc: '수리 요청, 귀책 판정, 정기점검 관리' },
                    { icon: '📦', title: '이관/폐기', desc: '양산이관 체크리스트, 금형 폐기 승인' },
                    { icon: '👥', title: '협력사 관리', desc: '제작처/생산처 업체 등록 및 장비 현황' },
                    { icon: '📈', title: '통계 리포트', desc: 'KPI 대시보드, 금형 현황 분석' }
                  ].map((item, i) => (
                    <div key={i} className="bg-gray-50 rounded-lg p-2.5">
                      <div className="flex items-center gap-1.5 mb-1">
                        <span className="text-sm">{item.icon}</span>
                        <span className="text-xs font-semibold text-gray-900">{item.title}</span>
                      </div>
                      <p className="text-[11px] text-gray-500">{item.desc}</p>
                    </div>
                  ))}
                </div>
              </section>

              {/* 사용자 역할별 안내 */}
              <section>
                <h4 className="text-sm font-bold text-gray-900 mb-2 flex items-center gap-1.5">👤 사용자 역할</h4>
                <div className="space-y-1.5">
                  {[
                    { role: '시스템 관리자', desc: '전체 시스템 설정, 사용자/업체 관리, 승인 처리, 기준값 관리', color: 'bg-red-50 text-red-700' },
                    { role: '금형개발 담당', desc: '금형 등록/개발 관리, 체크리스트 승인, 협력사 관리', color: 'bg-blue-50 text-blue-700' },
                    { role: '제작처', desc: '담당 금형 개발 진행, QR 코드 부착, 수리 대응', color: 'bg-orange-50 text-orange-700' },
                    { role: '생산처', desc: '일상/정기점검 실시, 수리 요청, 이관 관리', color: 'bg-green-50 text-green-700' }
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-2 text-sm">
                      <span className={`px-2 py-0.5 rounded text-[10px] font-bold shrink-0 ${item.color}`}>{item.role}</span>
                      <span className="text-gray-600 text-xs">{item.desc}</span>
                    </div>
                  ))}
                </div>
              </section>

              {/* 업체 프로필 등록 가이드 */}
              <section>
                <h4 className="text-sm font-bold text-gray-900 mb-2 flex items-center gap-1.5">🏢 업체 프로필 등록 방법</h4>
                <p className="text-xs text-gray-500 mb-3">제작처/생산처 담당자가 자사 정보를 등록하고 승인받는 과정입니다.</p>

                {/* 등록 흐름 시각화 */}
                <div className="flex items-center justify-between mb-4 px-2">
                  {[
                    { step: '1', label: '접속', icon: '🔑', color: 'bg-blue-500' },
                    { step: '2', label: '정보입력', icon: '✏️', color: 'bg-indigo-500' },
                    { step: '3', label: '임시저장', icon: '💾', color: 'bg-yellow-500' },
                    { step: '4', label: '승인요청', icon: '📤', color: 'bg-purple-500' },
                    { step: '5', label: '승인완료', icon: '✅', color: 'bg-green-500' }
                  ].map((s, i) => (
                    <div key={i} className="flex items-center">
                      <div className="flex flex-col items-center">
                        <div className={`w-8 h-8 ${s.color} text-white rounded-full flex items-center justify-center text-xs font-bold shadow`}>{s.step}</div>
                        <span className="text-[10px] text-gray-600 mt-1 font-medium">{s.label}</span>
                        <span className="text-sm mt-0.5">{s.icon}</span>
                      </div>
                      {i < 4 && <div className="w-6 h-0.5 bg-gray-300 mx-0.5 mb-5" />}
                    </div>
                  ))}
                </div>

                {/* 섹션별 입력 항목 */}
                <div className="space-y-2">
                  {/* Step 1: 메뉴 접근 */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-2.5">
                    <div className="flex items-center gap-2 mb-1.5">
                      <span className="w-5 h-5 bg-blue-500 text-white rounded-full flex items-center justify-center text-[10px] font-bold shrink-0">1</span>
                      <span className="text-xs font-bold text-blue-900">메뉴 접근</span>
                    </div>
                    <p className="text-[11px] text-blue-700 ml-7">로그인 후 좌측 메뉴에서 <strong>「업체 프로필」</strong>을 클릭합니다.</p>
                    <div className="ml-7 mt-1 flex items-center gap-1 text-[10px] text-blue-600">
                      <span className="px-1.5 py-0.5 bg-blue-100 rounded font-mono">대시보드</span>
                      <span>→</span>
                      <span className="px-1.5 py-0.5 bg-blue-200 rounded font-mono font-bold">업체 프로필</span>
                    </div>
                  </div>

                  {/* Step 2: 기본 정보 */}
                  <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-2.5">
                    <div className="flex items-center gap-2 mb-1.5">
                      <span className="w-5 h-5 bg-indigo-500 text-white rounded-full flex items-center justify-center text-[10px] font-bold shrink-0">2</span>
                      <span className="text-xs font-bold text-indigo-900">정보 입력 (5개 섹션)</span>
                    </div>
                    <p className="text-[11px] text-indigo-700 ml-7 mb-2">우측 상단 <strong>「수정」</strong> 버튼 클릭 후 아래 섹션을 작성합니다.</p>
                    <div className="ml-7 space-y-1.5">
                      {/* 기본정보 */}
                      <div className="bg-white rounded border border-indigo-100 p-2">
                        <div className="flex items-center gap-1.5 mb-1">
                          <span className="text-xs">🏠</span>
                          <span className="text-[11px] font-bold text-gray-900">기본 정보</span>
                          <span className="text-[9px] px-1 py-0.5 bg-red-100 text-red-600 rounded font-bold">필수</span>
                        </div>
                        <div className="grid grid-cols-3 gap-1 text-[10px] text-gray-600">
                          <span>• 업체명</span><span>• 사업자번호</span><span>• 대표자명</span>
                          <span>• 업태/업종</span><span>• 주소</span><span>• 연락처</span>
                        </div>
                      </div>
                      {/* 담당자 */}
                      <div className="bg-white rounded border border-indigo-100 p-2">
                        <div className="flex items-center gap-1.5 mb-1">
                          <span className="text-xs">👤</span>
                          <span className="text-[11px] font-bold text-gray-900">담당자 정보</span>
                          <span className="text-[9px] px-1 py-0.5 bg-blue-100 text-blue-600 rounded">선택</span>
                        </div>
                        <p className="text-[10px] text-gray-600">담당자를 추가하여 이름, 직책, 연락처, 이메일을 입력합니다.</p>
                      </div>
                      {/* 장비현황 */}
                      <div className="bg-white rounded border border-indigo-100 p-2">
                        <div className="flex items-center gap-1.5 mb-1">
                          <span className="text-xs">⚙️</span>
                          <span className="text-[11px] font-bold text-gray-900">장비 보유현황</span>
                          <span className="text-[9px] px-1 py-0.5 bg-blue-100 text-blue-600 rounded">선택</span>
                        </div>
                        <div className="text-[10px] text-gray-600 space-y-0.5">
                          <p><strong>사출기:</strong> 「기초정보 불러오기」로 마스터에서 선택하거나 수동 입력 (제조사, 모델, 톤수)</p>
                          <p><strong>일반장비:</strong> 「기초정보에서 선택」으로 CNC, EDM 등 카테고리별 장비를 추가</p>
                        </div>
                      </div>
                      {/* 인증 */}
                      <div className="bg-white rounded border border-indigo-100 p-2">
                        <div className="flex items-center gap-1.5 mb-1">
                          <span className="text-xs">📜</span>
                          <span className="text-[11px] font-bold text-gray-900">인증 정보</span>
                          <span className="text-[9px] px-1 py-0.5 bg-blue-100 text-blue-600 rounded">선택</span>
                        </div>
                        <p className="text-[10px] text-gray-600">IATF 16949, ISO 9001 등 보유 인증과 유효기간을 등록합니다.</p>
                      </div>
                      {/* 비고 */}
                      <div className="bg-white rounded border border-indigo-100 p-2">
                        <div className="flex items-center gap-1.5">
                          <span className="text-xs">📝</span>
                          <span className="text-[11px] font-bold text-gray-900">비고</span>
                          <span className="text-[9px] px-1 py-0.5 bg-gray-100 text-gray-500 rounded">선택</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Step 3~4: 저장 & 승인 */}
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-2.5">
                    <div className="flex items-center gap-2 mb-1.5">
                      <span className="w-5 h-5 bg-yellow-500 text-white rounded-full flex items-center justify-center text-[10px] font-bold shrink-0">3</span>
                      <span className="text-xs font-bold text-yellow-900">임시저장</span>
                    </div>
                    <p className="text-[11px] text-yellow-700 ml-7">작성 중간에 <strong>「임시저장」</strong> 버튼으로 데이터를 저장할 수 있습니다. 아직 승인 절차가 시작되지 않습니다.</p>
                  </div>

                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-2.5">
                    <div className="flex items-center gap-2 mb-1.5">
                      <span className="w-5 h-5 bg-purple-500 text-white rounded-full flex items-center justify-center text-[10px] font-bold shrink-0">4</span>
                      <span className="text-xs font-bold text-purple-900">승인 요청</span>
                    </div>
                    <p className="text-[11px] text-purple-700 ml-7 mb-1.5">입력 완료 후 <strong>「승인요청」</strong> 버튼을 클릭하고 승인자를 선택합니다.</p>
                    <div className="ml-7 bg-white rounded border border-purple-100 p-1.5 text-[10px]">
                      <div className="flex items-center gap-2 text-purple-700">
                        <span className="px-1.5 py-0.5 bg-purple-100 rounded font-bold">승인요청</span>
                        <span>→</span>
                        <span>승인자 선택</span>
                        <span>→</span>
                        <span>제출</span>
                        <span>→</span>
                        <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded">⏳ 대기</span>
                      </div>
                    </div>
                  </div>

                  {/* Step 5: 승인/반려 */}
                  <div className="bg-green-50 border border-green-200 rounded-lg p-2.5">
                    <div className="flex items-center gap-2 mb-1.5">
                      <span className="w-5 h-5 bg-green-500 text-white rounded-full flex items-center justify-center text-[10px] font-bold shrink-0">5</span>
                      <span className="text-xs font-bold text-green-900">승인 완료 / 반려</span>
                    </div>
                    <div className="ml-7 space-y-1 text-[11px]">
                      <div className="flex items-start gap-1.5">
                        <span className="text-green-600 font-bold shrink-0">✅ 승인:</span>
                        <span className="text-green-700">프로필이 확정되어 시스템에 반영됩니다.</span>
                      </div>
                      <div className="flex items-start gap-1.5">
                        <span className="text-red-500 font-bold shrink-0">❌ 반려:</span>
                        <span className="text-red-600">반려 사유를 확인 후 수정하여 재제출할 수 있습니다.</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* 팁 */}
                <div className="mt-3 bg-amber-50 border border-amber-200 rounded-lg p-2.5">
                  <h5 className="text-[11px] font-bold text-amber-900 mb-1">💡 등록 팁</h5>
                  <ul className="text-[10px] text-amber-700 space-y-0.5 list-disc list-inside">
                    <li>사출기는 <strong>「기초정보 불러오기」</strong>를 활용하면 마스터 데이터에서 빠르게 선택할 수 있습니다.</li>
                    <li>일반장비도 <strong>「기초정보에서 선택」</strong>으로 CNC, EDM 등을 카테고리별로 추가할 수 있습니다.</li>
                    <li>프로필 변경 시 금형개발 담당자에게 <strong>자동 알림</strong>이 발송됩니다.</li>
                    <li>승인 대기 중에도 재제출이 가능하며, 이전 요청이 갱신됩니다.</li>
                  </ul>
                </div>
              </section>

              {/* 키보드 단축키 */}
              <section>
                <h4 className="text-sm font-bold text-gray-900 mb-2 flex items-center gap-1.5">⌨️ 키보드 단축키</h4>
                <div className="grid grid-cols-2 gap-1.5 text-xs">
                  {[
                    { key: 'Ctrl + H', action: '대시보드 이동' },
                    { key: 'Ctrl + M', action: '금형 목록 이동' },
                    { key: 'Ctrl + R', action: '리포트 이동' },
                    { key: 'Ctrl + K', action: '단축키 도움말' }
                  ].map((item, i) => (
                    <div key={i} className="flex items-center justify-between bg-gray-50 rounded px-2.5 py-1.5">
                      <span className="text-gray-600">{item.action}</span>
                      <kbd className="px-1.5 py-0.5 bg-white border rounded text-[10px] font-mono">{item.key}</kbd>
                    </div>
                  ))}
                </div>
              </section>

              {/* 문의 */}
              <section className="bg-blue-50 rounded-lg p-3">
                <h4 className="text-sm font-bold text-blue-900 mb-1">💬 문의 및 지원</h4>
                <p className="text-xs text-blue-700">시스템 사용 중 문의사항이 있으시면 시스템 관리자에게 문의해주세요.</p>
                <p className="text-[10px] text-blue-500 mt-1">버전: CAMS v2.0 | 최종 업데이트: 2026.04</p>
              </section>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
